import {
  Box,
  Typography,
  Card,
  IconButton,
  Grid,
  CardActions,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useCallback,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  deleteCommunicationTemplates,
  getAllCommunicationTemplates,
  getCommunicationTemplateversions,
} from "./CommunicationTemplatesActions";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import DeleteIcon from "@mui/icons-material/Delete";
import { RootState } from "../../store/store";
import CommunicationTemplatesStyles from "./CommunicationTemplatesStyles";
import { ICreatedTemplateData } from "../../models/admin/communicationTemplates/communicatinTemplates";
import CustomSkeleton from "../../common/CustomSkeleton/CustomSkeleton";
import { t } from "i18next";

interface ICommunicationTemplatesListProps {
  setIsEdit: Dispatch<SetStateAction<boolean>>;
  setOpenCreateTemplateDrawer: Dispatch<SetStateAction<boolean>>;
  setOpenVersionHistoryDialog: Dispatch<SetStateAction<boolean>>;
  setOpenViewTemplateDialog: Dispatch<SetStateAction<boolean>>;
  setSelectedTemplate: Dispatch<SetStateAction<ICreatedTemplateData | null>>;
}

export const formatDate = (inputDate: string) => {
  if (!inputDate) return "N/A";
  const date = new Date(inputDate);
  return date.toLocaleDateString("default", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const CommunicationTemplatesList: FC<ICommunicationTemplatesListProps> = ({
  setIsEdit,
  setOpenCreateTemplateDrawer,
  setOpenVersionHistoryDialog,
  setOpenViewTemplateDialog,
  setSelectedTemplate,
}) => {
  const dispatch = useAppDispatch();
  const { isLoading, calledReducerType, isSuccess } = useAppSelector(
    (state: RootState) => state.common
  );

  const { allCommunicationTemplates } = useAppSelector(
    (store: RootState) => store.communicationTemplates
  );
  const [alertDialogOpen, setAlertDialogOpen] = useState<boolean>(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  const handleAlertDialogCancelClick = useCallback(() => {
    setAlertDialogOpen(false);
  }, []);

  const handleAlertDialogOkClick = useCallback(() => {
    if (selectedTemplateId) {
      dispatch(deleteCommunicationTemplates(selectedTemplateId));
    }
    setAlertDialogOpen(false);
  }, [selectedTemplateId]);

  useEffect(() => {
    dispatch(getAllCommunicationTemplates());
  }, [
    dispatch,
    isSuccess && calledReducerType === "delete/communicationTemplates",
  ]);

  const viewTemplate = useCallback(
    (template: ICreatedTemplateData) => {
      if (!template) return;
      setSelectedTemplate(template);
      setOpenViewTemplateDialog(true);
    },
    [setSelectedTemplate, setOpenViewTemplateDialog]
  );

  const editTemplate = useCallback(
    (template: ICreatedTemplateData) => {
      setSelectedTemplate(template);
      setIsEdit(true);
      setOpenCreateTemplateDrawer(true);
    },
    [setSelectedTemplate, setIsEdit, setOpenCreateTemplateDrawer]
  );

  const deleteTemplate = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setAlertDialogOpen(true);
  };

  const getVersionHistory = useCallback(
    (template: ICreatedTemplateData) => {
      if (!template) return;
      setSelectedTemplate(template);
      dispatch(getCommunicationTemplateversions(template.id));
      setOpenVersionHistoryDialog(true);
    },
    [setSelectedTemplate, dispatch, setOpenVersionHistoryDialog]
  );

  if (!allCommunicationTemplates?.length) {
    return (
      <Box 
        sx={{ marginTop: "1rem" }}
        id="no_templates_container"
        data-test-id="no_templates_container"
        aria-label="No Templates Available"
      >
        <Typography id="no_templates_text" data-test-id="no_templates_text" aria-label="No Templates Yet. Create one!">
          No Templates Yet. Create one!
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ marginTop: "1rem" }}
      id="templates_list_container"
      data-test-id="templates_list_container"
      aria-label="Templates List"
    >
      <Grid container spacing={2}>
        {isLoading && calledReducerType === "getAll/communicationTemplates" ? (
          <CustomSkeleton 
            width="270px" 
            height={150}
          />
        ) : (
          allCommunicationTemplates?.map((template) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={template?.id}>
              <Card 
                sx={CommunicationTemplatesStyles.templateCardContainer}
                id={`template_card_${template?.id}`}
                data-test-id={`template_card_${template?.id}`}
                aria-label={`Template: ${template.templateName}`}
              >
                <CardContent sx={CommunicationTemplatesStyles.cardContent}>
                  <Typography
                    variant="body1"
                    component="h3"
                    sx={{
                      ...CommunicationTemplatesStyles.templateCardTitle,
                      maxHeight: "50px",
                      overflow: "hidden",
                    }}
                    id={`template_name_${template?.id}`}
                    data-test-id={`template_name_${template?.id}`}
                    aria-label={`Template Name: ${template.templateName}`}
                  >
                    {template.templateName}
                  </Typography>
                </CardContent>
                <CardActions
                  sx={CommunicationTemplatesStyles.cardActionsFooter}
                >
                  <Box sx={CommunicationTemplatesStyles.cardActionButtons}>
                    <IconButton
                      size="small"
                      color="primary"
                      title="View template"
                      onClick={() => viewTemplate(template)}
                      id={`view_template_button_${template?.id}`}
                      data-test-id={`view_template_button_${template?.id}`}
                      aria-label={`View Template: ${template.templateName}`}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      title="Edit template"
                      onClick={() => editTemplate(template)}
                      id={`edit_template_button_${template?.id}`}
                      data-test-id={`edit_template_button_${template?.id}`}
                      aria-label={`Edit Template: ${template.templateName}`}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      title="View version history"
                      onClick={() => getVersionHistory(template)}
                      id={`version_history_button_${template?.id}`}
                      data-test-id={`version_history_button_${template?.id}`}
                      aria-label={`View Version History: ${template.templateName}`}
                    >
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      title="Delete template"
                      onClick={() => deleteTemplate(template?.id)}
                      id={`delete_template_button_${template?.id}`}
                      data-test-id={`delete_template_button_${template?.id}`}
                      aria-label={`Delete Template: ${template.templateName}`}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box
                    sx={CommunicationTemplatesStyles.dateAndVersionContainer}
                  >
                    <Typography 
                      sx={CommunicationTemplatesStyles.updatedAtText}
                      id={`template_updated_at_${template?.id}`}
                      data-test-id={`template_updated_at_${template?.id}`}
                      aria-label={`Last Updated: ${formatDate(template?.updatedAt)}`}
                    >
                      Last Updated: {formatDate(template?.updatedAt)}
                    </Typography>
                    <Chip
                      label={`version ${template?.version}`}
                      size="small"
                      sx={CommunicationTemplatesStyles.versionChipStyles}
                      id={`template_version_${template?.id}`}
                      data-test-id={`template_version_${template?.id}`}
                      aria-label={`Version: ${template?.version}`}
                    />
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
      <Dialog 
        open={alertDialogOpen}
        id="delete_template_dialog"
        data-test-id="delete_template_dialog"
        aria-label="Delete Template Confirmation"
      >
        <DialogTitle 
          sx={{ fontSize: "1rem" }}
          id="delete_template_dialog_title"
          data-test-id="delete_template_dialog_title"
          aria-label="Delete Template Confirmation"
        >
          {"Are you sure, you want to delete this template?"}
        </DialogTitle>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleAlertDialogCancelClick}
            sx={{ borderRadius: "4px" }}
            size="small"
            id="cancel_delete_button"
            data-test-id="cancel_delete_button"
            aria-label="Cancel Delete"
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="outlined"
            onClick={handleAlertDialogOkClick}
            sx={{ borderRadius: "4px" }}
            size="small"
            id="confirm_delete_button"
            data-test-id="confirm_delete_button"
            aria-label="Confirm Delete"
          >
            {t("Ok")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommunicationTemplatesList;
